
import Vue from "vue";
import {Purchase} from "@/models/base";
import AppAwsFilesEdit from "@/components/aws-files-modules/AwsFilesEdit.vue";
import AppOrderPreviewContent from "@/components/inbox/OrderPreviewContent.vue";
import {PurchaseAwsFile} from "@/models/base/purchase-aws-file";
import {PurchaseService} from "@/services";
import {AwsDocument} from "@/models/base/aws-document";
import {Order} from "@/utils/peppol/order";
import {parseUblXml} from "@/utils/peppol/ubl-xml-parser";
import { v4 } from "uuid";

interface IFileWrapper {
	key:string;
	url:string;
	file?:File;
	fileName:string;
	awsFile?:PurchaseAwsFile;
}

export default Vue.extend({
	data() {
		return {
			fileList: [] as IFileWrapper[],
			documentTab: 0,
			peppolOrder: null as Order | null,
			loadingPeppolOrder: false
		};
	},
	props: {
		value: {
			type: Object as () => Purchase,
			required: true
		},
		previewOnly: {
			type: Boolean,
			default: false
		},
	},
	computed: {
		tabOffset():number {
			return this.previewOnly ? 0 : 1;
		},
		documentTabs():string[]{
			let names = this.fileList.map(f=>f.awsFile?.IsOriginal ? `★ ${f.fileName}` : f.fileName);
			if (this.previewOnly) {
				return names as string[];
			}
			return [
				this.$ct("common.documents").toString(),
				...names,
			] as string[];
		},
		currentFileIsOriginal():boolean{
			if (!this.previewOnly && this.documentTab == 0) return false;
			let file = this.fileList[this.documentTab - this.tabOffset];
			return !!file?.awsFile?.IsOriginal;
		},
	},
	watch: {
		"value.UnprocessedFiles.length": {
			handler(){
				this.recreateFileList();
			}
		},
		"value.Documents.length": {
			handler(){
				this.recreateFileList();
			}
		},
		documentTab: {
			handler(){
				this.loadCurrentTab();
			}
		}
	},
	methods: {
		loadCurrentTab(){
			if (!this.previewOnly && this.documentTab == 0) return;
			let file = this.fileList[this.documentTab - this.tabOffset];
			if (!file) return;
			if (file.url) return;
			if (file.file){
				file.url = URL.createObjectURL(file.file);
			}else if (file.awsFile){
				file.url = file.awsFile.getUrl();
			}
		},
		getFileNameWithoutExtension(name:string):string{
			let parts = name.split(".");
			parts.pop();
			return parts.join(".");
		},
		recreateFileListUnprocessedFiles():IFileWrapper[] {
			let result:IFileWrapper[] = [];
			for (let file of this.value.UnprocessedFiles){
				let found = false;
				for (let existingFile of this.fileList) {
					if (existingFile.file == file){
						result.push(existingFile);
						found = true;
						break;
					}
				}
				if (found) continue;

				let newFile:IFileWrapper = {
					fileName: this.getFileNameWithoutExtension(file.name),
					file,
					key: v4(),
					url: ""
				};
				result.push(newFile);
			}
			return result;
		},
		recreateFileListDocuments():IFileWrapper[]{
			let result:IFileWrapper[] = [];
			for (let file of this.value.Documents){
				let found = false;
				for (let existingFile of this.fileList) {
					if (existingFile.awsFile == file){
						result.push(existingFile);
						found = true;
						break;
					}
				}
				if (found) continue;

				let newFile:IFileWrapper = {
					fileName: this.getFileNameWithoutExtension(file.getName()),
					awsFile: file,
					key: v4(),
					url: ""
				};
				result.push(newFile);
			}
			return result;
		},
		recreateFileList(){
			let result:IFileWrapper[] = [
				...this.recreateFileListUnprocessedFiles(),
				...this.recreateFileListDocuments()
			];
			// cleanup unused files
			for (let i = this.fileList.length-1; i>=0;i--) {
				let file = this.fileList[i];
				if (!file.url) continue;
				let foundIndex = result.findIndex(f=>f.key == file.key);
				if (foundIndex == -1 && file.file)  {
					URL.revokeObjectURL(file.url);
				}
			}
			this.fileList = result;
		},
		isXmlFile(file: IFileWrapper): boolean {
			if (file.file) {
				return file.file.name.toLowerCase().endsWith(".xml");
			}
			if (file.awsFile?.File) {
				const name = file.awsFile.File.Name.toLowerCase();
				const contentType = file.awsFile.File.ContentType;
				return name.endsWith(".xml") || contentType === "application/xml" || contentType === "text/xml";
			}
			return false;
		},
		async fetchPeppolOrder() {
			// Find the first XML file
			const xmlFile = this.fileList.find(f => this.isXmlFile(f));
			if (!xmlFile) return;

			this.loadingPeppolOrder = true;
			try {
				let xmlContent: string | null = null;

				// Get XML content from File object or fetch from URL
				if (xmlFile.file) {
					xmlContent = await xmlFile.file.text();
				} else if (xmlFile.awsFile) {
					const url = xmlFile.awsFile.getUrl();
					const response = await fetch(url);
					if (response.ok) {
						xmlContent = await response.text();
					}
				}

				if (!xmlContent) return;

				// Check if it's a Peppol/UBL document
				if (!this.isPeppolDocument(xmlContent)) return;

				// Convert UBL to Order (client-side, same as inbox preview)
				this.peppolOrder = parseUblXml(xmlContent);
			} catch {
				// Failed to fetch or parse XML
			} finally {
				this.loadingPeppolOrder = false;
			}
		},
		async onMarkOriginal(doc: AwsDocument){
			const makeOriginal = !doc.IsOriginal;
			for (let d of this.value.Documents){
				d.IsOriginal = makeOriginal && d.ID === doc.ID;
			}
			await PurchaseService.setOriginalDocument(this.value, doc.ID, makeOriginal);
		},
		isPeppolDocument(xmlContent: string): boolean {
			// Check for common Peppol/UBL root elements
			return xmlContent.includes("urn:oasis:names:specification:ubl") ||
				xmlContent.includes("<Invoice") ||
				xmlContent.includes("<CreditNote") ||
				xmlContent.includes("urn:cen.eu:en16931");
		},
	},
	mounted(){
		this.recreateFileList();
		this.loadCurrentTab();
		this.fetchPeppolOrder();
	},
	destroyed(){
		for (let file of this.fileList){
			if (file.url && file.file){
				URL.revokeObjectURL(file.url);
			}
		}
	},
	components: {
		AppAwsFilesEdit,
		AppOrderPreviewContent,
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
